import { Link, useLocation, useParams, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const getMenuItems = (t: any, projectId: string, navigate: any) => [
  {
    name: 'BackToPlatform',
    displayName: t('items.backToPlatform'),
    action: () => navigate('/projects'),
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
      </svg>
    ),
    isButton: true
  },
  {
    name: 'Divider1',
    isDivider: true
  },
  {
    name: 'Information',
    displayName: t('items.projectInformation'),
    href: `/projects/${projectId}/information`,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  {
    name: 'Members',
    displayName: t('items.projectMembers'),
    href: `/projects/${projectId}/members`,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    )
  },
  {
    name: 'Requests',
    displayName: t('items.projectRequests'),
    href: `/projects/${projectId}/requests`,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  },
  {
    name: 'Divider2',
    isDivider: true
  },
  {
    name: 'Products',
    displayName: t('items.products'),
    href: `/projects/${projectId}/products`,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    subItems: [
      {
        name: 'PipelineStudio',
        displayName: 'Pipeline Studio',
        href: `/projects/${projectId}/products/pipeline-studio`,
        icon: (
          <img src="/PS.png" alt="Pipeline Studio" className="w-5 h-5 rounded" />
        )
      }
    ]
  }
]

interface ProjectSidebarProps {
  isCollapsed: boolean
}

export default function ProjectSidebar({ isCollapsed }: ProjectSidebarProps) {
  const { t } = useTranslation('projectSidebar')
  const location = useLocation()
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [expandedItems, setExpandedItems] = useState<string[]>(['Products'])
  
  const menuItems = getMenuItems(t, projectId || '', navigate)

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    )
  }

  const isItemActive = (item: any) => {
    // Check if current path matches the item's main href
    if (item.href && location.pathname === item.href) {
      return true
    }
    // Check if any sub-item matches
    if (item.subItems) {
      return item.subItems.some((subItem: any) => {
        if (location.pathname === subItem.href) {
          return true
        }
        if (location.pathname.startsWith(subItem.href + '/')) {
          return true
        }
        return false
      })
    }
    return false
  }

  const isSubItemActive = (href: string) => {
    if (location.pathname === href) {
      return true
    }
    if (location.pathname.startsWith(href + '/')) {
      return true
    }
    return false
  }

  return (
    <aside
      className={`bg-white/90 backdrop-blur-xl border-r border-gray-200/50 transition-all duration-300 ease-in-out flex flex-col ${
        isCollapsed ? 'w-16' : 'w-56'
      }`}
    >
      {/* Main Menu */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          // Render divider
          if (item.isDivider) {
            return (
              <div key={item.name} className="my-3">
                <div className="h-px bg-gray-200/50" />
              </div>
            )
          }

          // Render button (Back to Platform)
          if (item.isButton) {
            return (
              <div key={item.name}>
                <button
                  onClick={item.action}
                  className={`w-full flex items-center ${
                    isCollapsed ? 'justify-center px-2' : 'justify-between px-3'
                  } py-3 rounded-lg transition-all duration-200 group text-gray-700 hover:bg-gray-100/50 hover:text-primary`}
                  title={isCollapsed ? item.displayName : ''}
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="shrink-0 text-gray-600 group-hover:text-primary">
                      {item.icon}
                    </div>
                    {!isCollapsed && (
                      <span className="font-medium text-sm truncate">{item.displayName}</span>
                    )}
                  </div>
                </button>
              </div>
            )
          }

          // Render items with subItems (Products)
          if (item.subItems) {
            return (
              <div key={item.name}>
                {isCollapsed ? (
                  // When collapsed, render as simple clickable link
                  <Link
                    to={item.href}
                    className={`flex items-center justify-center px-2 py-3 rounded-lg transition-all duration-200 group ${
                      isItemActive(item)
                        ? 'bg-primary/10 text-primary shadow-sm'
                        : 'text-gray-700 hover:bg-gray-100/50 hover:text-primary'
                    }`}
                    title={item.displayName}
                  >
                    <div className={`shrink-0 ${isItemActive(item) ? 'text-primary' : 'text-gray-600 group-hover:text-primary'}`}>
                      {item.icon}
                    </div>
                  </Link>
                ) : (
                  // When expanded, render with separate link and expand button
                  <div className={`flex items-center rounded-lg ${
                    isItemActive(item)
                      ? 'bg-primary/10 text-primary shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100/50 hover:text-primary'
                  } transition-all duration-200 group`}>
                    <Link
                      to={item.href}
                      className="flex-1 flex items-center px-3 py-3"
                    >
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className={`shrink-0 ${isItemActive(item) ? 'text-primary' : 'text-gray-600 group-hover:text-primary'}`}>
                          {item.icon}
                        </div>
                        <span className="font-medium text-sm truncate">{item.displayName}</span>
                      </div>
                    </Link>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleExpanded(item.name)
                      }}
                      className="px-3 py-3 hover:bg-primary/5 rounded-r-lg transition-colors"
                    >
                      <svg
                        className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                          expandedItems.includes(item.name) ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                )}
                {!isCollapsed && expandedItems.includes(item.name) && (
                  <div className="ml-6 mt-1 space-y-0.5 border-l-2 border-gray-200/50 pl-3">
                    {item.subItems.map((subItem: any) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href}
                        className={`flex items-center px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                          isSubItemActive(subItem.href)
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-gray-600 hover:bg-gray-100/50 hover:text-primary'
                        }`}
                      >
                        <span className="shrink-0">{subItem.icon}</span>
                        <span className="ml-3 truncate">{subItem.displayName}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )
          }

          // Render regular items
          return (
            <div key={item.name}>
              <Link
                to={item.href || ''}
                className={`flex items-center ${
                  isCollapsed ? 'justify-center px-2' : 'justify-between px-3'
                } py-3 rounded-lg transition-all duration-200 group ${
                  isItemActive(item)
                    ? 'bg-primary/10 text-primary shadow-sm'
                    : 'text-gray-700 hover:bg-gray-100/50 hover:text-primary'
                }`}
                title={isCollapsed ? item.displayName : ''}
              >
                <div className="flex items-center space-x-3 min-w-0">
                  <div className={`shrink-0 ${isItemActive(item) ? 'text-primary' : 'text-gray-600 group-hover:text-primary'}`}>
                    {item.icon}
                  </div>
                  {!isCollapsed && (
                    <span className="font-medium text-sm truncate">{item.displayName}</span>
                  )}
                </div>
              </Link>
            </div>
          )
        })}
      </nav>
    </aside>
  )
}
